import type { RestrictionSet } from '@ben/shared/types/restriction-set';
import { convertSignedUrlsToRedirectUrls } from '@ben/shared/hooks/use-upload-rich-text-image';
import { mergeWith } from 'lodash';

import { RESTRICTION_TYPE_KEYS } from './restriction-set/types';

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  !!value && typeof value === 'object' && !Array.isArray(value);

const isRichTextField = (key: string): boolean =>
  key.startsWith('content') || key.startsWith('description');

/**
 * Checks if a restrictionSet object is empty (all restriction arrays are empty or missing).
 * An empty restrictionSet should be sent as null to avoid creating empty records on the backend.
 */
export const isEmptyRestrictionSet = (
  value: RestrictionSet | Record<string, unknown>
): boolean => {
  // If it has an id, it's an existing restriction set - don't treat as empty
  if ('id' in value) return false;

  const hasAnyRestrictions = RESTRICTION_TYPE_KEYS.some((key) => {
    const array = value[key];
    return Array.isArray(array) && array.length > 0;
  });

  return !hasAnyRestrictions;
};

/**
 * sanitizePayload
 * - drops controller-only keys (e.g. dateRange)
 * - converts empty strings to null
 * - converts signed URLs to redirect URLs in rich text fields
 * - recurses into arrays and objects
 */
export const sanitizePayload = (values: unknown): unknown => {
  if (values === null || values === undefined) return values;
  if (!isPlainObject(values) && !Array.isArray(values)) return values;

  const excludeKeys = new Set(['dateRange']);

  if (Array.isArray(values)) {
    return values.map((item) => sanitizePayload(item));
  }

  const result: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(values)) {
    if (excludeKeys.has(key)) continue;
    if (value === undefined) continue;
    if (typeof value === 'string' && value.length === 0) {
      result[key] = null;
      continue;
    }
    if (typeof value === 'string' && isRichTextField(key)) {
      result[key] = convertSignedUrlsToRedirectUrls(value);
      continue;
    }
    // Convert empty restrictionSet objects to null to avoid creating empty records
    if (key === 'restrictionSet' && isPlainObject(value)) {
      result[key] = isEmptyRestrictionSet(value)
        ? null
        : sanitizePayload(value);
      continue;
    }
    if (Array.isArray(value) || isPlainObject(value)) {
      result[key] = sanitizePayload(value);
      continue;
    }
    result[key] = value;
  }
  return result;
};
/**
 * deepMergeArraysAndObjects
 * - For arrays: replace target arrays with source arrays
 * - For objects: deep merge recursively
 * - For primitives: overwrite
 */
export const deepMergeArraysAndObjects = (
  target: unknown,
  ...sources: unknown[]
): unknown => {
  let output = isPlainObject(target) ? { ...target } : {};

  for (const source of sources) {
    if (!source) continue;
    if (isArray(source)) return cloneArray(source);
    if (isPlainObject(source)) {
      output = mergeWith(output, source, (_, sourceValue) => {
        if (Array.isArray(sourceValue)) {
          return cloneArray(sourceValue);
        }
        return undefined;
      });
    }
  }

  return output;
};

const isArray = (value: unknown): value is unknown[] => Array.isArray(value);
const cloneArray = (array: unknown[]): unknown[] => [...array];

// Helper utilities for deep path value setting used by getDirtyValues
const isNumericSegment = (segment: string) => /^\d+$/.test(segment);
const selectKey = (segment: string): string | number =>
  isNumericSegment(segment) ? Number(segment) : segment;

const setAt = (
  container: Record<string, unknown> | unknown[],
  key: string | number,
  assignedValue: unknown
) => {
  if (Array.isArray(container)) {
    container[Number(key)] = assignedValue;
    return;
  }
  container[String(key)] = assignedValue;
};

const getOrInitNext = (
  container: Record<string, unknown> | unknown[],
  key: string | number,
  nextSegment: string | undefined
): Record<string, unknown> | unknown[] => {
  const hasNext = typeof nextSegment === 'string';
  const shouldCreateArray = hasNext && isNumericSegment(nextSegment);
  if (Array.isArray(container)) {
    const index = Number(key);
    if (container[index] === undefined) {
      container[index] = shouldCreateArray ? [] : {};
    }
    return container[index] as Record<string, unknown>;
  }
  const stringKey = String(key);
  const record = container;
  if (record[stringKey] === undefined) {
    record[stringKey] = shouldCreateArray ? [] : {};
  }
  return record[stringKey] as unknown[];
};

const setDeep = (
  object: Record<string, unknown>,
  path: string[],
  value: unknown
) => {
  if (path.length === 0) return;
  let cursor: Record<string, unknown> | unknown[] = object;
  for (let index = 0; index < path.length - 1; index += 1) {
    const key = selectKey(path[index]);
    const nextSegment = path[index + 1];
    cursor = getOrInitNext(cursor, key, nextSegment);
  }
  const lastKey = selectKey(path.at(-1) ?? '');
  setAt(cursor, lastKey, value);
};

const walkDirtyTree = (
  node: unknown,
  path: string[],
  getValues: (name: string) => unknown,
  result: Record<string, unknown>
) => {
  if (node === true || Array.isArray(node)) {
    const name = path.join('.');
    setDeep(result, path, getValues(name));
    return;
  }
  if (isPlainObject(node)) {
    for (const [key, child] of Object.entries(node)) {
      if (child) walkDirtyTree(child, [...path, key], getValues, result);
    }
  }
};

/**
 * getDirtyValues
 * - Reads form.formState.dirtyFields and returns the current values at those paths
 * - If any element in an array is dirty, returns the entire array
 */
export const getDirtyValues = (form: {
  formState: { dirtyFields: unknown };
  getValues: (name: string) => unknown;
}): unknown => {
  const result: Record<string, unknown> = {};

  walkDirtyTree(form.formState.dirtyFields, [], form.getValues, result);
  return result;
};

/**
 * findFirstErrorPath
 * - Traverses a react-hook-form errors object and returns the first invalid field path
 * - Arrays are traversed in index order and contribute numeric segments (e.g. "subPrices.0.name")
 * - Objects are traversed by key insertion order
 */
export const findFirstErrorPath = (
  errors: Record<string, unknown>
): string | undefined => {
  return findFirstErrorPathVisit(errors, []);
};

// Narrower check for a react-hook-form error leaf (has a string message)
const isReactHookFormErrorLeaf = (node: Record<string, unknown>) =>
  isPlainObject(node) && typeof node.message === 'string';

// Traverses a react-hook-form errors object to find the first invalid field path
const findFirstErrorPathVisit = (
  node: Record<string, unknown>,
  pathSegments: string[]
): string | undefined => {
  if (!node) return undefined;

  if (Array.isArray(node)) {
    let index = 0;
    for (const item of node) {
      const result = findFirstErrorPathVisit(item as Record<string, unknown>, [
        ...pathSegments,
        String(index),
      ]);
      if (result) return result;
      index += 1;
    }
    return undefined;
  }

  if (isReactHookFormErrorLeaf(node)) {
    return pathSegments.join('.');
  }

  if (isPlainObject(node)) {
    for (const key of Object.keys(node)) {
      const childPath = findFirstErrorPathVisit(
        node[key] as Record<string, unknown>,
        [...pathSegments, key]
      );
      if (childPath) return childPath;
    }
  }

  return undefined;
};

/**
 * buildReadableErrorPath
 * - Converts a dot-notated path (e.g. "additionalConfig.subPrices.0.name")
 *   into a human-friendly breadcrumb (e.g. "Sub prices > #1 > Name")
 * - Options:
 *   - segmentLabelMap: map of raw keys to user-facing labels
 *   - hideSegments: list of raw keys to remove from the final breadcrumb
 */
export const buildReadableErrorPath = (
  path: string,
  hideSegments?: string[]
): string => {
  const segments = path
    .split('.')
    .filter((segment) => !hideSegments?.includes(segment));

  if (segments.length === 0) return '';

  const readableSegments = segments.map((segment) =>
    humanizePathSegment(segment)
  );

  return readableSegments.join(' > ');
};

// Converts snakeCase/camelCase to Title Case
const toTitleCase = (value: string): string =>
  value
    .replaceAll('_', ' ')
    .replaceAll(/[\da-z]([A-Z])/g, (match) =>
      match.replaceAll(/([\da-z])([A-Z])/g, '$1 $2')
    )
    .replaceAll(/\s+/g, ' ')
    .trim()
    .replaceAll(/\b\w/g, (c) => c.toUpperCase());

// Builds a user-facing label for a single path segment
const humanizePathSegment = (segment: string): string => {
  if (/^\d+$/.test(segment)) return `#${Number(segment) + 1}`;
  return toTitleCase(segment);
};

interface OptionWithEligibilityRule {
  eligibilityRule?: {
    isActive?: boolean;
  };
}

/**
 * Checks if any option in the provided array has an active eligibility rule
 * @param options - Array of options that may contain eligibility rules
 * @returns true if any option has an active eligibility rule, false otherwise
 */
export const hasActiveEligibilityRule = (
  options: OptionWithEligibilityRule[] | null | undefined
): boolean => {
  if (!options || options.length === 0) return false;

  return options.some((option) => option?.eligibilityRule?.isActive === true);
};
